import isArray from "lodash/isArray";
import isEmpty from "lodash/isEmpty";
import every from "lodash/every";
import MarkdownRenderer from "@/components/UI/MarkdownRenderer";
import { SqlExplanation } from "@/components/Chat/ChatSQLCard";
import ChatProtocolCard from "@/components/Chat/ChatProtocolCard";
import type { SelectedSources } from "@/components/Chat/chat";
import { formatSelectedSources } from "@/utils/selectedSourcesFormatter";

type InsightRun = {
  av_explanation?: string | null;
  selected_sources?: SelectedSources;
};

const isEmptyArray = (array: unknown) =>
  !isArray(array) || isEmpty(array) || every(array, isEmpty);

/**
 * A run's explanation, told once. When the run has SQL sources, each
 * source's explanation is shown beside the query it describes (the run-level
 * `av_explanation` repeats that same text, so it is not shown as well);
 * otherwise the run-level explanation stands alone.
 */
export default function InsightRunExplanation({ run }: { run: InsightRun }) {
  const rawSQLData =
    run.selected_sources?.SQLAgentSchema ||
    run.selected_sources?.vivo_data_views;
  const hasSQLData = !!rawSQLData && !isEmptyArray(rawSQLData);
  const protocolData = run.selected_sources?.ProtocolAgentSchema;
  const hasProtocolData = !!protocolData && !isEmptyArray(protocolData);

  if (!hasSQLData && !hasProtocolData && !run.av_explanation) {
    return (
      <p className="text-sm text-[#00000073] px-2 py-1">
        No explanation available.
      </p>
    );
  }

  const sqlSources = hasSQLData ? formatSelectedSources(run) : null;

  return (
    <div className="flex flex-col gap-3 text-sm px-2 py-1">
      {sqlSources ? (
        <SqlExplanation
          explanation={sqlSources.explanation.trim()}
          query={sqlSources.sql_query.trim()}
          resultTables={sqlSources.result_tables}
        />
      ) : (
        run.av_explanation && (
          <div className="markdown text-[#556C75]">
            <MarkdownRenderer content={run.av_explanation} />
          </div>
        )
      )}
      {hasProtocolData && (
        <ChatProtocolCard sources={protocolData} chatExplanation={false} />
      )}
    </div>
  );
}
